import React, { useEffect, useState } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import { useHistory } from 'react-router-dom';

import { DropdownButton, Dropdown } from 'react-bootstrap';
import {
  Card,
  CardHeader,
  CardBody,
  Col,
  Row,
  CardTitle,
} from 'reactstrap';

import ReactTable from '../../../../components/ReactTable/ReactTable';
import { useDispatch, useSelector } from 'react-redux';
import { AppState } from '../../../../store/store';
import ApiCaller from '../../../../lib/ApiCaller';
import NavigationExamApi from '../../../../api/NavigationExamApi';

import {
  DEFAULT_TIMEZONE_FORMAT,
  formatDateTime,
  sortDateTable,
} from '../../../../shared/utils/dateTime';
import MYDocument from '../../../../lib/common/models/myDocument';
import Exam from '../../../../lib/common/models/exam';
import { stateListNames } from 'store/reducers/listsReducer';
import {
  setCurrentDoc,
  setCurrentDrafts,
  setCurrentExam,
} from 'store/actions/docs/docsActions';
import { useClearListStateOnPageChange, useTableControls } from 'hooks';

import 'react-toastify/dist/ReactToastify.css';
import LoadingSpinner from 'views/components/loadingSpinner/loadingSpinner';
import useLoading from 'hooks/useLoading';
import DocumentTrailModal from 'views/components/documentTrail/DocumentTrailModal';

const NavigationExamDraftList: React.FC = () => {
  const loggedUser = useSelector((state: AppState) => state.session.userInfo);
  const layout = useSelector((state: AppState) => state.session.layout);
  const docs: MYDocument[] = useSelector(
    (state: AppState) => state.current.drafts,
  );
  const { loading, setLoading, loadingTip, setLoadingTip } = useLoading();
  const navigationExamApi = new NavigationExamApi(new ApiCaller(loggedUser.token));

  const listName = stateListNames.examDrafts;
  const [isTrailOpen, setIsTrailOpen] = useState<boolean>(false);
  const [trailDocumentId, setTrailDocumentId] = useState<string | undefined>(undefined);

  const {
    filters,
    sorting,
    pagination,
    onChangeFilters,
    onChangeSorting,
    onChangePagination,
  } = useTableControls(listName);

  const history = useHistory();
  const dispatch = useDispatch();

  useEffect(() => {
    const token = loggedUser.token;
    const examApi = new NavigationExamApi(new ApiCaller());
    examApi
      .getDrafts(token)
      .then((item) => {
        dispatch(setCurrentDrafts(item));
      })
      .catch((err) => {
        console.error(err);
      });
  }, []);

  useClearListStateOnPageChange(listName, [
    "/navigation_exam/edit",
    "/navigation_exam/generated/list",
  ]);

  const removeExam = (id: string) => {
    const token = loggedUser.token;
    const examApi = new NavigationExamApi(new ApiCaller(loggedUser.token));
    setLoading(true);
    setLoadingTip('Removing exam draft...');
    examApi
      .removeDraft(id, token)
      .then((item) => {
        examApi
          .getDrafts(token)
          .then((item) => {
            dispatch(setCurrentDrafts(item));
          })
          .catch((err) => {
            console.error(err);
          });
      })
      .catch((err) => {
        console.error(err);
      }).finally(() => {
        setLoading(false);
      }
      );
  };

  const launchEditing: any = async (row: MYDocument) => {
    await loadAdditionalData(row);
    history.push(`/${layout}/navigation_exam/edit`);
  };

    /**
   * Load full data of document
   * @param slug
   */
    function loadAdditionalData(document:MYDocument){
      setLoading(true)
      setLoadingTip('Loading exam data...')
      return Promise.all([
        navigationExamApi.getDraftById(document.id),
        navigationExamApi.getExamModelBySlug(document.sourceSlug as string)
      ]).then(([examDoc, exam]) => {
        dispatch(setCurrentDoc(new MYDocument(examDoc)));
        dispatch(setCurrentExam(new Exam(exam)));
      }).catch((err) => {
        console.error(err);
        toast.error('Error loading exam draft');
      }).finally(() => {
        setLoading(false);
      });
    }

  const publish: any = (id: string) => {
    const examApi = new NavigationExamApi(new ApiCaller(loggedUser.token));
    const token = loggedUser.token;
    setLoading(true);
    setLoadingTip('Publishing exam draft...');
    examApi
      .publishDraft(id, token)
      .then((item) => {

        examApi
          .getDrafts(token)
          .then((item) => {
            dispatch(setCurrentDrafts(item));
          })
          .catch((err) => {
            console.error(err);
          });
      })
      .catch((err) => {
        console.error(err);
        toast.error('Error publishing exam draft');
      }).finally(() => {
        setLoading(false);
      }
      );
  };

  const MCAReview: any = (id: string) => {
    const examApi = new NavigationExamApi(new ApiCaller(loggedUser.token));
    const token = loggedUser.token;
    setLoading(true);
    setLoadingTip('Sending draft to MCA review...');
    examApi
      .SETOMCAReviewDraft(id, token)
      .then((item) => {

        examApi
          .getDrafts(token)
          .then((item) => {
            dispatch(setCurrentDrafts(item));
          })
          .catch((err) => {
            console.error(err);
          });
      })
      .catch((err) => {
        console.error(err);
        toast.error('Error sending draft to MCA Review');
      }).finally(() => {
        setLoading(false);
      }
    );
  };

  const columns = [
    { Header: 'SERIAL NO.', accessor: (d: any) => d.serialNumber ? d.serialNumber : '---', sortable: true },
    { Header: 'EXAM ID', accessor: (d: any) => d.customName, sortable: true },
    {
      Header: 'EXAM CENTRE',
      accessor: (d: any) => d.customDescription,
      sortable: true,
    },
    { Header: 'STATUS', accessor: (d: any) => d.status, sortable: true },
    {
      Header: 'DATE',
      accessor: (d: any) =>
        formatDateTime(d.expirationDate, DEFAULT_TIMEZONE_FORMAT),
      sortable: true,
      sortType: (a: any, b: any) => sortDateTable(a, b, 'expirationDate'),
    },
    {
      Header: 'ACTIONS',
      Cell: (row: any) => {
        const isSETO = row.row.original.syllabus?.code === 'S04' && row.row.original.module?.code === 'M02';
        const isInProgress = row.row.original.status === 'IN_PROGRESS';

        return (
          loggedUser.hasAdminRole() &&
            <DropdownButton variant="default" title="Actions" size="sm">
              {isInProgress && (
              <Dropdown.Item
                style={{margin: 0}}
                onClick={() => launchEditing(row.row.original)}>
                <i className="nc-icon nc-settings" />
                &nbsp;&nbsp;EDIT
              </Dropdown.Item>
              )}
              <Dropdown.Item onClick={() => { setTrailDocumentId(row.row.original.id); setIsTrailOpen(true); }}>
                <i className="nc-icon nc-time-alarm" />
                &nbsp;&nbsp;DOCUMENT TRAIL
              </Dropdown.Item>
              {isInProgress && (
                isSETO ? (
                  <Dropdown.Item onClick={() => MCAReview(row.row.original.id)}>
                    <i className="nc-icon nc-send" />
                    &nbsp;&nbsp;SEND TO MCA
                  </Dropdown.Item>
                ) : (
                  <Dropdown.Item onClick={() => publish(row.row.original.id)}>
                    <i className="nc-icon nc-send" />
                    &nbsp;&nbsp;PUBLISH
                  </Dropdown.Item>
                )
              )}
              { loggedUser.isAdmin() &&
                <Dropdown.Item onClick={() => removeExam(row.row.original.id)}>
                  <i className="nc-icon nc-simple-delete" />
                  &nbsp;&nbsp;REMOVE
                </Dropdown.Item>
              }
            </DropdownButton>
        );
      },
    },
  ];

  return (
    <>
      <div className="content">
      <LoadingSpinner spinning={loading} tip={loadingTip}>
        <Row>
          <Col md="12">
            <Card>
              <CardHeader>
                <CardTitle tag="h4">{'Exam Drafts'}</CardTitle>
              </CardHeader>
              <CardBody>
                {docs && (
                  <ReactTable
                    data={docs}
                    columns={columns}
                    initialFilters={filters}
                    onChangeFilters={onChangeFilters}
                    initialSorting={sorting}
                    onChangeSorting={onChangeSorting}
                    initialPagination={pagination}
                    onChangePagination={onChangePagination}
                  />
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
        </LoadingSpinner>
        <ToastContainer />
      </div>
      <DocumentTrailModal isOpen={isTrailOpen} onClose={() => setIsTrailOpen(false)} documentId={trailDocumentId} />
    </>
  );
};

export default NavigationExamDraftList;
